import React, { useMemo } from "react";

function parsePages(value, maxPage) {
  const clean = [];
  const parts = String(value || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const part of parts) {
    if (part.includes("-")) {
      const [aRaw, bRaw] = part.split("-");
      const a = Number(aRaw);
      const b = Number(bRaw);
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue;

      const start = Math.max(1, Math.min(a, b));
      const end = Math.max(a, b);

      for (let n = start; n <= end; n += 1) {
        if ((!maxPage || n <= maxPage) && !clean.includes(n)) {
          clean.push(n);
        }
      }
    } else {
      const n = Number(part);
      if (
        Number.isFinite(n) &&
        n >= 1 &&
        (!maxPage || n <= maxPage) &&
        !clean.includes(n)
      ) {
        clean.push(n);
      }
    }
  }

  return clean.sort((a, b) => a - b);
}

const pagesToText = (pages) => (Array.isArray(pages) ? pages.join(",") : "");

export default function ReportPdfPagesPicker({
  availablePdfDocuments = [],
  selectedPdfPages = [],
  onChange,
}) {
  const selectedByDoc = useMemo(() => {
    const map = new Map();
    for (const item of selectedPdfPages || []) {
      map.set(Number(item.document_id), item);
    }
    return map;
  }, [selectedPdfPages]);

  const replaceDocSelection = (doc, nextItemOrNull) => {
    const next = (selectedPdfPages || []).filter(
      (x) => Number(x.document_id) !== Number(doc.id),
    );

    if (nextItemOrNull) {
      next.push(nextItemOrNull);
    }

    onChange?.(next);
  };

  const updateDocPages = (doc, text) => {
    const pages = parsePages(text, doc.page_count);

    if (pages.length > 0) {
      replaceDocSelection(doc, {
        document_id: doc.id,
        pages,
        all_pages: false,
        insert_after: "photos",
      });
    } else {
      replaceDocSelection(doc, null);
    }
  };

  const toggleWholePdf = (doc, checked) => {
    if (checked) {
      replaceDocSelection(doc, {
        document_id: doc.id,
        pages: [],
        all_pages: true,
        insert_after: "photos",
      });
    } else {
      replaceDocSelection(doc, null);
    }
  };

  if (!availablePdfDocuments.length) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500">
        No uploaded PDF documents are available for this inspection.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3">
      <div>
        <div className="text-sm font-semibold text-gray-900">
          Insert uploaded PDF pages into final report
        </div>
        <div className="text-xs text-gray-500">
          Choose “Merge whole PDF” or enter selected pages like <b>1,3,5</b> or{" "}
          <b>2-4</b>. Only PDFs attached to this inspection are shown.
        </div>
      </div>

      {availablePdfDocuments.map((doc) => {
        const current = selectedByDoc.get(Number(doc.id));
        const wholePdfChecked = !!current?.all_pages;

        return (
          <div
            key={doc.id}
            className="grid grid-cols-1 gap-3 rounded-md border border-gray-100 p-3 md:grid-cols-[1fr_180px_220px] md:items-center"
          >
            <div>
              <div className="text-sm font-medium text-gray-900">
                {doc.document_name}
              </div>
              <div className="text-xs text-gray-500">
                {doc.page_count
                  ? `${doc.page_count} page(s)`
                  : "Page count unavailable"}
                {doc.file_url ? (
                  <>
                    {" • "}
                    <a
                      className="text-blue-600 underline"
                      href={doc.file_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open PDF
                    </a>
                  </>
                ) : null}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={wholePdfChecked}
                onChange={(e) => toggleWholePdf(doc, e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Merge whole PDF</span>
            </label>

            <input
              type="text"
              value={wholePdfChecked ? "" : pagesToText(current?.pages)}
              disabled={wholePdfChecked}
              onChange={(e) => updateDocPages(doc, e.target.value)}
              placeholder="e.g. 1,3,5 or 2-4"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        );
      })}
    </div>
  );
}